import { Nullable } from 'src/core/common/domain/types/types';
import { Timezone } from './timezone';

/**
 * The zone a date falls back to when nothing more specific says otherwise: the one where the tenant
 * operates, not the one the device is set to. A task planned for 9:00 in Madrid must read 9:00 for
 * everybody, because the commitment is against the clock on the wall of the service address.
 *
 * Tasks now carry their own zone and no longer depend on this, but every other date in the app still
 * does, and so does a task stored before the API started sending the field.
 *
 * This is deliberately static state with a single writer (MasterDataService). LocalDate is built
 * through static factories from entities that cannot take an injected dependency, so threading the
 * zone through DI would mean rewriting every call site and injecting a service into Task and
 * DateRange. The zone was already global here; it is only mutable now, and validated on the way in.
 *
 * Anything created before MasterDataService has initialised falls back to DEFAULT. Before login
 * there is no tenant, so that is the correct answer rather than a gap.
 */
export class TenantTimezone {
  // Kept as an alias so callers reading the tenant zone do not have to know about Timezone.
  static readonly DEFAULT = Timezone.DEFAULT;

  private static timezone: string = TenantTimezone.DEFAULT;

  static current(): string {
    return TenantTimezone.timezone;
  }

  static set(timezone: Nullable<string>): void {
    TenantTimezone.timezone = Timezone.firstSupported(timezone);
  }

  static reset(): void {
    TenantTimezone.timezone = TenantTimezone.DEFAULT;
  }
}
